import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop/ScrollToTop.jsx';
import Starfield from './components/Starfield/Starfield';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import Home from './modules/Home/Home';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute/AdminProtectedRoute';
import { AuthProvider } from './hooks/useAuth';
// Importar estilos globales (incluye variables y animaciones)
import './global.css';

// ⚡ LAZY LOADING - Componentes pesados cargados bajo demanda
// Esto difiere ~500KB de Three.js hasta que el usuario visite Dashboard
const Server = lazy(() => import('./modules/Server/Server'));
const UserDashboard = lazy(() => import('./pages/Dashboard/UserDashboard'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword/ResetPasswordPage'));

// 📰 NEWS ADMIN - Panel de administración de noticias
const NewsAdmin = lazy(() => import('./pages/Admin/NewsAdmin'));
const NewsEditor = lazy(() => import('./pages/Admin/NewsEditor'));
const NewsArticle = lazy(() => import('./pages/News/NewsArticle'));

// 📜 LEGAL PAGES - Términos y Privacidad
const TermsPage = lazy(() => import('./pages/Legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/Legal/PrivacyPage'));

// Componente de loading para Suspense
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    color: '#00ff00',
    fontSize: '1rem',
    fontFamily: 'Arial, sans-serif'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(0,255,0,0.2)',
        borderTop: '3px solid #00ff00',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <span>Cargando...</span>
    </div>
  </div>
);

/**
 * App Component - TECNILAND Main Entry Point
 * DESKTOP-FIRST: Arquitectura optimizada para 1920px+
 * MOBILE-OPTIMIZED: Starfield desactivado en móvil para mejor performance
 * OPTIMIZED: Lazy loading para rutas pesadas (Dashboard, Server)
 */
function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detectar cambios de viewport para mostrar/ocultar Starfield
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        {/* Starfield solo en desktop para optimizar performance móvil */}
        {!isMobile && <Starfield density={150} />}
        <div className="App">
          <Navbar />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/server" element={<Server />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* 📰 PUBLIC NEWS ROUTES */}
                <Route path="/news/:slug" element={<NewsArticle />} />
                
                {/* � LEGAL PAGES */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                
                {/* �🔒 ADMIN NEWS ROUTES - Requieren rol admin */}
                <Route 
                  path="/admin/news" 
                  element={
                    <AdminProtectedRoute>
                      <NewsAdmin />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/news/new" 
                  element={
                    <AdminProtectedRoute>
                      <NewsEditor />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/news/edit/:slug" 
                  element={
                    <AdminProtectedRoute>
                      <NewsEditor />
                    </AdminProtectedRoute>
                  } 
                />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
